// Slice B2.4-D — subscribe to peek fires and persist cross-session (best-effort).

import { useEffect } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import {
  ACTIVITY_DOCK_PEEK_EVENT,
  type ActivityDockPeekEventDetail,
} from "@/components/activity/activityDockPeekEvent";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import { persistActivityDockPeekTelemetry } from "@/lib/activityDock/peekTelemetryClient";

/**
 * Mount once where the dock is live. Listens to B2.4-B window events and
 * POSTs each fire to Supabase via the service-role API. No-ops when
 * unauthenticated or SSR.
 */
export function useActivityDockPeekPersistence(currentMode: PrimaryMode | null | undefined): void {
  const auth = useAuthOptional();
  const accessToken = (auth?.session as { access_token?: string } | null)?.access_token ?? null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!accessToken || auth?.status !== "ready") return;
    const token = accessToken;

    function handler(event: Event): void {
      const detail = (event as CustomEvent<ActivityDockPeekEventDetail>).detail;
      if (!detail || typeof detail !== "object") return;
      if (typeof detail.reasonSectionId !== "string") return;
      if (typeof detail.mode !== "string") return;

      const bucketId = getBucketForMode(detail.mode);
      persistActivityDockPeekTelemetry({
        detail,
        bucketId,
        accessToken: token,
      });
    }

    window.addEventListener(ACTIVITY_DOCK_PEEK_EVENT, handler);
    return () => window.removeEventListener(ACTIVITY_DOCK_PEEK_EVENT, handler);
  }, [accessToken, auth?.status, currentMode]);
}
